import React, { useState, useEffect } from 'react';
import { getAllTeams } from '../utils/teams';
import JudgeLayout from '../layouts/JudgeLayout';
import { FiSearch, FiX, FiUsers, FiLayers, FiEye, FiMail, FiUser } from 'react-icons/fi';

const JudgeTeams = () => {
  const [teams, setTeams] = useState([]);
  const [filteredTeams, setFilteredTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  // eslint-disable-next-line no-unused-vars
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTeam, setSelectedTeam] = useState(null);

  useEffect(() => {
    fetchTeams();
  }, []);

  useEffect(() => {
    const filtered = teams.filter(team =>
      team.team_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      team.leader?.full_name?.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredTeams(filtered);
  }, [searchTerm, teams]);

  const fetchTeams = async () => {
    try {
      setLoading(true);
      const result = await getAllTeams();
      if (result.success) {
        setTeams(result.data);
        setFilteredTeams(result.data);
      } else {
        setError(result.message);
      }
    } catch (err) {
      console.error('Error fetching teams:', err);
      setError('حدث خطأ في جلب بيانات الفرق');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'مقبول':
        return { bg: 'bg-green-500/20', text: 'text-green-400', border: 'border-green-500' };
      case 'مرفوض':
        return { bg: 'bg-red-500/20', text: 'text-red-400', border: 'border-red-500' };
      default:
        return { bg: 'bg-yellow/20', text: 'text-yellow', border: 'border-yellow' };
    }
  };

  if (loading) {
    return (
      <JudgeLayout>
        <div className="flex justify-center items-center min-h-[80vh]">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-yellow"></div>
        </div>
      </JudgeLayout>
    );
  }

  return (
    <JudgeLayout>
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-yellow mb-2">
          عرض <span className="text-white">الفرق</span>
        </h1>
        <p className="text-white/70">استعراض الفرق المشاركة في الهاكاثون</p>
      </div>

      <div className="bg-dark-blue border-2 border-yellow rounded-2xl p-6 mb-6">
        <div className="relative">
          <FiSearch className="absolute right-3 top-1/2 transform -translate-y-1/2 text-white/50" size={20} />
          <input
            type="text"
            placeholder="البحث باسم الفريق أو اسم القائد..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pr-10 pl-4 py-3 bg-blue border-2 border-white/20 text-white placeholder-white/50 rounded-lg focus:outline-none focus:border-yellow"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {filteredTeams.map((team) => {
          const statusColors = getStatusColor(team.status);
          return (
            <div key={team.id} className="bg-dark-blue border-2 border-yellow rounded-2xl overflow-hidden hover:border-white/50 transition-all duration-300">
              <div className="bg-yellow px-6 py-4">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className="bg-[#05339c] p-2 rounded-lg">
                      <FiLayers className="text-yellow" size={24} />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-[#05339c]">{team.team_name}</h3>
                      <p className="text-[#05339c]/70 text-sm mt-1">
                        {team.team_members?.length || 0} أعضاء
                      </p>
                    </div>
                  </div>
                  <span className={`px-3 py-1 rounded-full text-xs font-semibold border-2 ${statusColors.bg} ${statusColors.text} ${statusColors.border}`}>
                    {team.status}
                  </span>
                </div>
              </div>

              <div className="p-6 space-y-4">
                <div>
                  <h4 className="text-yellow text-sm font-medium mb-2 flex items-center gap-2">
                    <FiUser />
                    قائد الفريق
                  </h4>
                  <div className="bg-blue rounded-xl p-3 border border-white/10">
                    <div className="flex justify-between items-center mb-1">
                      <span className="text-white font-medium">{team.leader?.full_name}</span>
                    </div>
                    <div className="flex items-center gap-2 text-white/60 text-sm">
                      <FiMail size={14} />
                      <span>{team.leader?.email}</span>
                    </div>
                  </div>
                </div>

                <div>
                  <h4 className="text-yellow text-sm font-medium mb-2">فكرة المشروع</h4>
                  <div className="bg-blue rounded-xl p-3 border border-white/10 min-h-[80px]">
                    <p className="text-white/80 text-sm line-clamp-3 leading-relaxed">
                      {team.idea || 'لا يوجد وصف للمشروع'}
                    </p>
                  </div>
                </div>

                <div className="pt-4 border-t border-white/10">
                  <button
                    onClick={() => setSelectedTeam(team)}
                    className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-blue text-yellow border-2 border-yellow rounded-xl hover:bg-blue/80 transition-colors font-medium"
                  >
                    <FiEye size={18} />
                    <span>عرض التفاصيل والأعضاء</span>
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {filteredTeams.length === 0 && (
        <div className="bg-dark-blue border-2 border-yellow rounded-2xl p-12 text-center">
          <div className="w-16 h-16 bg-white/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <FiSearch className="text-white/50" size={32} />
          </div>
          <p className="text-white mb-2">لا توجد فرق مطابقة</p>
          <p className="text-white/60 text-sm">جرب تغيير مصطلحات البحث</p>
        </div>
      )}

      {selectedTeam && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-dark-blue border-2 border-yellow rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-yellow px-6 py-4 flex items-center justify-between z-10">
              <h3 className="text-xl font-bold text-[#05339c]">{selectedTeam.team_name}</h3>
              <button
                onClick={() => setSelectedTeam(null)}
                className="text-[#05339c] hover:bg-[#05339c] hover:text-yellow p-2 rounded-lg transition-all"
              >
                <FiX size={24} />
              </button>
            </div>

            <div className="p-6 space-y-6">
              <div>
                <h4 className="font-semibold text-yellow mb-3 flex items-center gap-2">
                  <FiUsers />
                  أعضاء الفريق
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {selectedTeam.team_members?.map((member) => (
                    <div key={member.applicant_id} className="bg-blue rounded-xl p-4 border-2 border-white/10">
                      <div className="flex items-center gap-3 mb-3">
                        <div className="w-10 h-10 bg-yellow/20 rounded-full flex items-center justify-center">
                          <FiUser className="text-yellow" size={20} />
                        </div>
                        <div>
                          <p className="font-bold text-white">{member.applicant?.full_name}</p>
                          <p className="text-xs text-yellow">
                            {member.applicant_id === selectedTeam.leader_id ? 'قائد الفريق' : 'عضو'}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 text-white/60 text-sm">
                        <FiMail size={14} />
                        <span>{member.applicant?.email}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <h4 className="font-semibold text-yellow mb-3">فكرة المشروع</h4>
                <div className="bg-blue rounded-xl p-4 border-2 border-white/10">
                  <p className="text-white/80 leading-relaxed whitespace-pre-wrap">{selectedTeam.idea}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </JudgeLayout>
  );
};

export default JudgeTeams;

